import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { getBrp, appendBrpLog, updateBrp, editBrpLogEntry, deleteBrpLogEntry, reviewAllBrp } from "../lib/api";
import { useAuth } from "../App";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";

const RISK_META = {
  high: { dot: "bg-red-500", cell: "bg-red-400", pill: "bg-red-100 text-red-800 border-red-300", label: "High" },
  medium: { dot: "bg-yellow-500", cell: "bg-yellow-200", pill: "bg-yellow-100 text-yellow-800 border-yellow-300", label: "Medium" },
  low: { dot: "bg-green-500", cell: "bg-green-200", pill: "bg-green-100 text-green-800 border-green-300", label: "Low" },
};

// Default text seeded into an empty Responsibilities box (clinician now lives in
// its own box above, linked to the student file).
const RESP_TEMPLATE = "Redirection: Everyone\n\nCheck Ins: Clinical";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso.length <= 10 ? iso + "T00:00:00" : iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return iso;
  }
};

// Click-to-edit text cell (goal / staff responsibilities). Saves on blur; Esc cancels.
// When empty and `emptyTemplate` is set, the template is displayed and seeded into
// the editor as a starting point (only saved once the user changes it).
function InlineText({ value, onSave, canEdit, placeholder, testId, emptyTemplate }) {
  const base = value || emptyTemplate || "";
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(base);
  useEffect(() => { setDraft(value || emptyTemplate || ""); }, [value, emptyTemplate]);

  const renderStatic = () => {
    if (value) return <span className="whitespace-pre-wrap text-sm text-gray-700">{value}</span>;
    if (emptyTemplate) return <span className="whitespace-pre-wrap text-sm text-gray-500">{emptyTemplate}</span>;
    return <span className="text-sm text-gray-400 italic">{placeholder}</span>;
  };

  if (!canEdit) {
    return value || emptyTemplate
      ? <div>{renderStatic()}</div>
      : <span className="text-gray-300">—</span>;
  }
  if (!editing) {
    return (
      <div
        onClick={() => setEditing(true)}
        className="cursor-text rounded px-1 py-0.5 -mx-1 hover:bg-[#00305E]/5 min-h-[24px]"
        data-testid={testId}
      >
        {renderStatic()}
      </div>
    );
  }
  const commit = () => {
    setEditing(false);
    if ((draft || "") !== (value || emptyTemplate || "")) onSave(draft || "");
  };
  return (
    <Textarea
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Escape") { setDraft(value || emptyTemplate || ""); setEditing(false); }
      }}
      rows={emptyTemplate ? 5 : 3}
      className="text-sm"
      data-testid={`${testId}-input`}
    />
  );
}

// Small icon action button used inside the running log.
function LogAction({ title, onClick, testId, children }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      data-testid={testId}
      className="text-gray-400 hover:text-[#00305E] p-0.5"
    >
      {children}
    </button>
  );
}

// Running log with expand/collapse, inline add-entry, and per-entry edit / hide / delete (editors).
function LogCell({ row, canEdit, onAppend, onEditEntry, onDeleteEntry }) {
  const [expanded, setExpanded] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState("");

  const all = row.log_entries || [];
  const shown = all.filter((e) => !e.hidden);
  const hidden = all.filter((e) => e.hidden);
  const visible = expanded ? shown : shown.slice(0, 1);

  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    setSaving(true);
    try {
      await onAppend(row.student_id, t);
      setText("");
      setAdding(false);
      setExpanded(true);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (e) => { setEditingId(e.id); setEditDraft(e.text || ""); };
  const saveEdit = async (e) => {
    const t = editDraft.trim();
    if (!t) return;
    setEditingId(null);
    if (t !== (e.text || "")) await onEditEntry(row.student_id, e.id, { text: t });
  };
  const removeEntry = (e) => {
    if (window.confirm("Remove this log entry? This cannot be undone.")) onDeleteEntry(row.student_id, e.id);
  };

  const Pencil = <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>;
  const EyeOff = <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/><path d="M6.61 6.61A13.53 13.53 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/><line x1="2" y1="2" x2="22" y2="22"/></svg>;
  const Eye = <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>;
  const Trash = <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>;

  return (
    <div data-testid={`brp-log-${row.student_id}`}>
      {shown.length === 0 && !adding && hidden.length === 0 && (
        <span className="text-sm text-gray-400 italic">No entries yet</span>
      )}
      <div className="space-y-3">
        {visible.map((e) => (
          <div key={e.id} className="text-sm">
            <div className="flex items-start justify-between gap-2">
              <div className="text-xs font-semibold text-[#00305E]">
                {fmtDate(e.date)}{e.author_name ? ` · ${e.author_name}` : ""}{e.edited ? " · edited" : ""}
              </div>
              {canEdit && editingId !== e.id && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <LogAction title="Edit entry" onClick={() => startEdit(e)} testId={`brp-log-edit-${e.id}`}>{Pencil}</LogAction>
                  <LogAction title="Collapse (hide) entry" onClick={() => onEditEntry(row.student_id, e.id, { hidden: true })} testId={`brp-log-hide-${e.id}`}>{EyeOff}</LogAction>
                  <LogAction title="Remove entry" onClick={() => removeEntry(e)} testId={`brp-log-delete-${e.id}`}>{Trash}</LogAction>
                </div>
              )}
            </div>
            {editingId === e.id ? (
              <div className="mt-1 space-y-1">
                <Textarea autoFocus value={editDraft} onChange={(ev) => setEditDraft(ev.target.value)} rows={3} className="text-sm" data-testid={`brp-log-edit-input-${e.id}`} />
                <div className="flex gap-2">
                  <Button size="sm" className="bg-[#00305E] hover:bg-[#00305E]/90 h-7 text-xs" onClick={() => saveEdit(e)} data-testid={`brp-log-edit-save-${e.id}`}>Save</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingId(null)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="whitespace-pre-wrap text-gray-700">{e.text}</div>
            )}
          </div>
        ))}
      </div>

      {shown.length > 1 && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-xs font-medium text-[#00305E] hover:underline"
          data-testid={`brp-log-toggle-${row.student_id}`}
        >
          {expanded ? "Show less" : `Show all ${shown.length} entries`}
        </button>
      )}

      {canEdit && hidden.length > 0 && (
        <div className="mt-2">
          <button
            onClick={() => setShowHidden((v) => !v)}
            className="text-xs font-medium text-gray-500 hover:underline"
            data-testid={`brp-log-hidden-toggle-${row.student_id}`}
          >
            {showHidden ? "Hide collapsed entries" : `Show ${hidden.length} collapsed`}
          </button>
          {showHidden && (
            <div className="mt-2 space-y-3 opacity-70">
              {hidden.map((e) => (
                <div key={e.id} className="text-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-xs font-semibold text-gray-500">{fmtDate(e.date)} · collapsed</div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <LogAction title="Unhide entry" onClick={() => onEditEntry(row.student_id, e.id, { hidden: false })} testId={`brp-log-unhide-${e.id}`}>{Eye}</LogAction>
                      <LogAction title="Remove entry" onClick={() => removeEntry(e)} testId={`brp-log-delete-${e.id}`}>{Trash}</LogAction>
                    </div>
                  </div>
                  <div className="whitespace-pre-wrap text-gray-600">{e.text}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {canEdit && (
        adding ? (
          <div className="mt-2 space-y-2">
            <Textarea
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="New entry — dated today, appended above the log"
              rows={3}
              className="text-sm"
              data-testid={`brp-log-input-${row.student_id}`}
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={submit}
                disabled={saving || !text.trim()}
                className="bg-[#00305E] hover:bg-[#00305E]/90 h-7 text-xs"
                data-testid={`brp-log-save-${row.student_id}`}
              >
                {saving ? "Saving…" : "Add entry"}
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setAdding(false); setText(""); }}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setAdding(true)}
            className="mt-2 flex items-center gap-1 text-xs font-medium text-[#00305E] hover:underline"
            data-testid={`brp-log-add-${row.student_id}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
            Add entry
          </button>
        )
      )}
    </div>
  );
}

export default function BrpPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ today: "", can_edit: false, pending_review_count: 0, rows: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [reviewing, setReviewing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getBrp();
      setData(res.data);
    } catch (err) {
      toast.error("Failed to load behavioral plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const canEdit = data.can_edit;

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data.rows;
    return data.rows.filter((r) => r.student_name.toLowerCase().includes(q));
  }, [data.rows, search]);

  const patchRowLocal = (studentId, patch) => {
    setData((d) => ({
      ...d,
      rows: d.rows.map((r) => (r.student_id === studentId ? { ...r, ...patch } : r)),
    }));
  };

  const saveField = async (studentId, field, value) => {
    try {
      const res = await updateBrp(studentId, { [field]: value });
      patchRowLocal(studentId, res.data);
      toast.success("Saved — all staff notified to review");
    } catch (err) {
      toast.error(err?.response?.status === 403 ? "Only clinical staff and admins can edit" : "Failed to save");
    }
  };

  const saveClinician = async (studentId, value) => {
    try {
      const res = await updateBrp(studentId, { assigned_clinician: value });
      patchRowLocal(studentId, res.data);
      toast.success("Clinician updated — synced to the student file");
    } catch (err) {
      toast.error(err?.response?.status === 403 ? "Only clinical staff and admins can edit" : "Failed to save");
    }
  };

  const reviewAll = async () => {
    setReviewing(true);
    try {
      const res = await reviewAllBrp();
      const n = res.data.reviewed;
      toast.success(`Reviewed ${n} update${n === 1 ? "" : "s"}`);
      await load();
    } catch (err) {
      toast.error("Failed to mark reviewed");
    } finally {
      setReviewing(false);
    }
  };

  const appendLog = async (studentId, text) => {
    const res = await appendBrpLog(studentId, text);
    patchRowLocal(studentId, { log_entries: res.data.log_entries, plan_id: res.data.plan_id });
    toast.success("Entry added — all staff notified to review");
  };

  const editLogEntry = async (studentId, entryId, patch) => {
    try {
      const res = await editBrpLogEntry(studentId, entryId, patch);
      patchRowLocal(studentId, { log_entries: res.data.log_entries });
      if (patch.text !== undefined) toast.success("Entry updated — all staff notified to review");
      else if (patch.hidden) toast.success("Entry collapsed");
      else toast.success("Entry restored");
    } catch (err) {
      toast.error("Failed to update entry");
    }
  };

  const deleteLogEntry = async (studentId, entryId) => {
    try {
      const res = await deleteBrpLogEntry(studentId, entryId);
      patchRowLocal(studentId, { log_entries: res.data.log_entries });
      toast.success("Entry removed — all staff notified to review");
    } catch (err) {
      toast.error("Failed to remove entry");
    }
  };

  return (
    <div className="p-6 max-w-full">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="text-3xl font-bold text-[#00305E]">BRP</h1>
        <div className="flex items-center gap-2 text-sm text-gray-600" data-testid="brp-today">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M3 10h18M8 2v4M16 2v4"/></svg>
          Today: <span className="font-semibold text-[#00305E]">{fmtDate(data.today)}</span>
        </div>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Behavioral Redirection Plans for all current students.
        {canEdit
          ? " Add dated log entries and edit fields inline — every change notifies all staff to review."
          : " Read-only — clinical staff and admins can edit."}
      </p>

      {data.pending_review_count > 0 && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3" data-testid="brp-review-banner">
          <div className="flex items-center gap-2 text-sm text-amber-900">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
            <span>
              <span className="font-bold" data-testid="brp-review-count">{data.pending_review_count}</span>{" "}
              plan update{data.pending_review_count === 1 ? " needs" : "s need"} your review — scan the highlighted rows below, then mark them all reviewed here.
            </span>
          </div>
          <Button
            onClick={reviewAll}
            disabled={reviewing}
            className="bg-amber-600 hover:bg-amber-700 text-white h-8 text-xs"
            data-testid="brp-review-all-btn"
          >
            {reviewing ? "Reviewing…" : "Mark all as reviewed"}
          </Button>
        </div>
      )}

      <div className="mb-4 max-w-sm">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by student name…"
          data-testid="brp-search"
        />
      </div>

      {loading ? (
        <div className="text-gray-500 py-12 text-center">Loading…</div>
      ) : (
        <div className="border border-gray-300 rounded-lg overflow-x-auto bg-white">
          <table className="w-full table-fixed border-collapse text-left" data-testid="brp-table">
            <colgroup>
              <col style={{ width: "13%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "40%" }} />
              <col style={{ width: "17%" }} />
              <col style={{ width: "18%" }} />
            </colgroup>
            <thead>
              <tr className="bg-[#dbe6f5] text-[#00305E] text-sm">
                <th className="border border-gray-300 px-3 py-2 font-bold text-center">Student</th>
                <th className="border border-gray-300 px-3 py-2 font-bold text-center">Dates of Review</th>
                <th className="border border-gray-300 px-3 py-2 font-bold text-center">Plan / Interventions</th>
                <th className="border border-gray-300 px-3 py-2 font-bold text-center">Goal</th>
                <th className="border border-gray-300 px-3 py-2 font-bold text-center">Staff Responsibilities</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-10 text-center text-gray-400">No students match “{search}”.</td></tr>
              )}
              {rows.map((r) => {
                const risk = RISK_META[r.risk_level] || null;
                return (
                  <tr key={r.student_id} className={`align-top ${r.pending_review ? "ring-2 ring-inset ring-amber-400" : ""}`} data-testid={`brp-row-${r.student_id}`}>
                    {/* Student & risk (cell tinted by risk level) */}
                    <td className={`border border-gray-300 px-3 py-3 align-top break-words ${risk ? risk.cell : ""}`}>
                      <Link
                        to={`/students/${r.student_id}?tab=clinical-plans`}
                        className="font-semibold text-[#00305E] hover:underline break-words"
                        data-testid={`brp-student-link-${r.student_id}`}
                      >
                        {r.student_name}
                      </Link>
                      {r.pending_review && (
                        <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-500 text-white align-middle" data-testid={`brp-pending-${r.student_id}`}>
                          Updated
                        </span>
                      )}
                      <div className="mt-2">
                        {canEdit ? (
                          <select
                            value={r.risk_level || ""}
                            onChange={(e) => saveField(r.student_id, "risk_level", e.target.value)}
                            className="text-xs border border-gray-400 rounded px-1.5 py-1 bg-white/90 w-full"
                            data-testid={`brp-risk-${r.student_id}`}
                          >
                            <option value="" disabled>Set risk…</option>
                            <option value="high">High risk</option>
                            <option value="medium">Medium risk</option>
                            <option value="low">Low risk</option>
                          </select>
                        ) : (
                          <span className="text-xs font-semibold text-gray-700">
                            {risk ? `${risk.label} risk` : "No plan"}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Dates of review */}
                    <td className="border border-gray-300 px-3 py-3 align-top break-words">
                      {canEdit ? (
                        <input
                          type="date"
                          value={r.review_date || ""}
                          onChange={(e) => saveField(r.student_id, "review_date", e.target.value)}
                          className="text-sm border rounded px-2 py-1 bg-white w-full"
                          data-testid={`brp-review-${r.student_id}`}
                        />
                      ) : (
                        <span className="text-sm text-gray-700">{r.review_date ? fmtDate(r.review_date) : <span className="text-gray-300">—</span>}</span>
                      )}
                    </td>

                    {/* Plan / interventions running log */}
                    <td className="border border-gray-300 px-3 py-3 align-top break-words">
                      <LogCell row={r} canEdit={canEdit} onAppend={appendLog} onEditEntry={editLogEntry} onDeleteEntry={deleteLogEntry} />
                    </td>

                    {/* Goal */}
                    <td className="border border-gray-300 px-3 py-3 align-top break-words">
                      <InlineText
                        value={r.goal}
                        canEdit={canEdit}
                        placeholder="Click to add a goal"
                        onSave={(v) => saveField(r.student_id, "goal", v)}
                        testId={`brp-goal-${r.student_id}`}
                      />
                    </td>

                    {/* Staff responsibilities: Clinician (linked to student file) + Responsibilities */}
                    <td className="border border-gray-300 px-3 py-3 align-top break-words">
                      <div className="space-y-3">
                        <div data-testid={`brp-clinician-box-${r.student_id}`}>
                          <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Clinician</div>
                          {canEdit ? (
                            <select
                              value={r.clinician || ""}
                              onChange={(e) => saveClinician(r.student_id, e.target.value)}
                              className="text-sm border border-gray-400 rounded px-1.5 py-1 bg-white w-full"
                              data-testid={`brp-clinician-${r.student_id}`}
                            >
                              <option value="">Unassigned</option>
                              {/* Preserve a legacy free-text/id value that isn't in the options list */}
                              {r.clinician && !(data.clinician_options || []).some((o) => o.id === r.clinician) && (
                                <option value={r.clinician}>{r.clinician_name || r.clinician}</option>
                              )}
                              {[...(data.clinician_options || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((o) => (
                                <option key={o.id} value={o.id}>{o.name}</option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-sm text-gray-700">
                              {r.clinician_name || <span className="text-gray-300">—</span>}
                            </span>
                          )}
                        </div>
                        <div className="border-t border-gray-200 pt-2">
                          <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Responsibilities</div>
                          <InlineText
                            value={r.staff_responsibilities}
                            canEdit={canEdit}
                            emptyTemplate={RESP_TEMPLATE}
                            placeholder="Who handles redirection, who handles check-ins…"
                            onSave={(v) => saveField(r.student_id, "staff_responsibilities", v)}
                            testId={`brp-staff-${r.student_id}`}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
